import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import SettingsOverviewCard from "../../molecules/SettingsOverviewCard"
import SatelliteIcon from "../../atoms/icons/SatelliteIcon"
import SignalStrengthLineChart from "../../molecules/charts/SignalStrengthLineChart"
import { SignalStrengthPoint, visibleTimeDomain } from "../../../hooks/useSignalStrengthHistory"
import { SECTION_OVERVIEW_SATELLITE_SIGNAL, SECTION_OVERVIEW_VEHICLE_ORIENTATION } from "../../../utils/pages"
import {
  MotorElevationChart,
  MotorOrientationChart,
  normalizeDegrees,
  signalStrengthToColor,
  vehicleHeadingFromAntenna,
} from "../../molecules/charts/MotorOrientationChart"
import styled, { useTheme } from "styled-components"
import H4 from "../../atoms/typography/H4"
import { HorizontalSpacer } from "../../atoms/spacers"
import BodyTextLarge from "../../atoms/typography/BodyTextLarge"
import BodyText from "../../atoms/typography/BodyText"
import { theme } from "../../theme"

const CardContent = styled.div`
  width: 100%;
`

const ChartContent = styled.div<{ $isUnavailable: boolean }>`
  filter: ${(props) => (props.$isUnavailable ? "grayscale(1)" : "none")};
  opacity: ${(props) => (props.$isUnavailable ? 0.45 : 1)};
  pointer-events: ${(props) => (props.$isUnavailable ? "none" : "auto")};
  transition:
    filter 200ms ease,
    opacity 200ms ease;
`

const ChartFrameWrapper = styled.div`
  margin-right: 2rem;
`

const OrientationChartFrame = styled.div`
  position: relative;
  width: fit-content;
`

const ElevationChartCorner = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  transform: scale(0.45) translateX(50%);
  transform-origin: bottom right;
`

const RadarSignalCard: React.FunctionComponent<{
  motorLocalOrientation?: number
  targetOrientation?: number
  targetSatelliteName?: string
  currentOrientation?: number
  currentElevation?: number
  otherSatelliteAngles: number[]
  signalStrengthHistory: SignalStrengthPoint[]
}> = ({
  motorLocalOrientation,
  targetOrientation,
  targetSatelliteName,
  currentOrientation,
  currentElevation,
  otherSatelliteAngles,
  signalStrengthHistory,
}) => {
  const activeTheme = useTheme()
  const chartTimeDomain = React.useMemo(() => visibleTimeDomain(signalStrengthHistory), [signalStrengthHistory])
  const latestSignalStrength = signalStrengthHistory[signalStrengthHistory.length - 1]?.signalStrength ?? 0
  const normalizedSignalStrength = Math.min(1, Math.max(0, latestSignalStrength / 100))
  const isUnavailable =
    motorLocalOrientation === undefined ||
    targetOrientation === undefined ||
    currentOrientation === undefined ||
    currentElevation === undefined ||
    signalStrengthHistory.length === 0
  // currentOrientation and targetOrientation are the antenna's actual and wanted compass azimuths,
  // so they coincide whenever the terminal is locked on. The dial is drawn relative to the vehicle,
  // which means the target has to be placed relative to the vehicle heading instead.
  const vehicleHeading =
    currentOrientation !== undefined && motorLocalOrientation !== undefined
      ? vehicleHeadingFromAntenna(currentOrientation, motorLocalOrientation)
      : undefined
  const targetLocalOrientation =
    vehicleHeading !== undefined && targetOrientation !== undefined
      ? normalizeDegrees(targetOrientation - vehicleHeading)
      : undefined

  return (
    <SettingsOverviewCard title={SECTION_OVERVIEW_VEHICLE_ORIENTATION.name} icon={<SatelliteIcon />}>
      <CardContent>
        <ChartContent $isUnavailable={isUnavailable} aria-busy={isUnavailable}>
          <Row>
            <Col>
              <H4>Status</H4>
              <BodyText>
                Jamming: <span>None</span>
              </BodyText>
              <BodyText>
                IDAR: <span style={{ color: activeTheme.colors.green }}>Active</span>
              </BodyText>
              <HorizontalSpacer spacing={2} />
            </Col>
          </Row>
          <Row align="center" justify="between">
            <Hidden xs sm md lg>
              <Col xs="content">
                <ChartFrameWrapper>
                  <OrientationChartFrame>
                    <MotorOrientationChart
                      targetLabel={targetSatelliteName ?? ""}
                      targetAngleDeg={targetLocalOrientation}
                      vehicleHeadingDeg={vehicleHeading}
                      coneRotationDeg={motorLocalOrientation}
                      coneColor={signalStrengthToColor(normalizedSignalStrength, {
                        blue: activeTheme.colors.blue,
                        blueBorder: activeTheme.gradients.blueBorder,
                      })}
                      otherSatelliteAngles={otherSatelliteAngles}
                    />
                    <ElevationChartCorner>
                      <MotorElevationChart
                        elevationDeg={currentElevation}
                        coneColor={activeTheme.colors.blue}
                        compact={true}
                      />
                    </ElevationChartCorner>
                  </OrientationChartFrame>
                </ChartFrameWrapper>
              </Col>
            </Hidden>
            <Visible xs sm md lg>
              <Col xs={12} sm={6} md={6}>
                <Row justify="center">
                  <MotorOrientationChart
                    targetLabel={targetSatelliteName ?? ""}
                    targetAngleDeg={targetLocalOrientation}
                    vehicleHeadingDeg={vehicleHeading}
                    coneRotationDeg={motorLocalOrientation}
                    coneColor={signalStrengthToColor(normalizedSignalStrength, {
                      blue: activeTheme.colors.blue,
                      blueBorder: activeTheme.gradients.blueBorder,
                    })}
                    otherSatelliteAngles={otherSatelliteAngles}
                  />
                </Row>
              </Col>
              <Col xs={12} sm={6} md={6}>
                <Row justify="center">
                  <MotorElevationChart elevationDeg={currentElevation} coneColor={activeTheme.colors.blue} />
                </Row>
              </Col>
            </Visible>
            <Visible xs md lg>
              <Col xs={12}>
                <HorizontalSpacer spacing={2} />
                <Row>
                  <Col>
                    <SignalStrengthLineChart points={signalStrengthHistory} timeDomain={chartTimeDomain} />
                  </Col>
                </Row>
              </Col>
            </Visible>
            <Hidden xs md lg>
              <Col>
                <Row>
                  <Col>
                    <SignalStrengthLineChart points={signalStrengthHistory} timeDomain={chartTimeDomain} />
                  </Col>
                </Row>
              </Col>
            </Hidden>
          </Row>
        </ChartContent>
      </CardContent>
    </SettingsOverviewCard>
  )
}

export default RadarSignalCard
